import React from 'react';
import { Spinner } from '../../../components/ui';
import { formatQuantity, groupChoices, type MaterialChoice } from '../utils/adjustInventory';

interface MaterialPickerProps {
  /** Id of the visible label, so the listbox has an accessible name. */
  labelledBy: string;
  choices: MaterialChoice[];
  value: string;
  onChange: (materialId: string) => void;
  /** When set, a search box is shown above the list (Receipt: pick anything in the catalogue). */
  search?: {
    query: string;
    onQueryChange: (query: string) => void;
    loading: boolean;
    placeholder: string;
  };
  /** Shown when `choices` is empty. */
  emptyMessage: string;
  invalid?: boolean;
  /** Reason a choice is the only one possible (e.g. the chosen bin holds it). */
  disabled?: boolean;
}

/**
 * A grouped list of materials with what is on hand beside each. A list rather than a select
 * because the on-hand figure is the point: it is what tells the user which material to move.
 */
const MaterialPicker: React.FC<MaterialPickerProps> = ({
  labelledBy,
  choices,
  value,
  onChange,
  search,
  emptyMessage,
  invalid = false,
  disabled = false,
}) => {
  const groups = groupChoices(choices);

  return (
    <div
      className={`tw-overflow-hidden tw-rounded tw-border tw-bg-surface ${
        invalid ? 'tw-border-danger' : 'tw-border-line-input'
      }`}
    >
      {search ? (
        <div className="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-line tw-px-3">
          <input
            type="search"
            value={search.query}
            onChange={(event) => search.onQueryChange(event.target.value)}
            placeholder={search.placeholder}
            aria-label="Search the material catalogue"
            className="tw-min-h-[2.5rem] tw-w-full tw-border-0 tw-bg-transparent tw-py-1.5 tw-text-sm tw-text-content placeholder:tw-text-content-muted focus:tw-outline-none"
          />
          {search.loading ? <Spinner size="sm" className="tw-shrink-0 tw-text-primary">Searching</Spinner> : null}
        </div>
      ) : null}

      <div role="listbox" aria-labelledby={labelledBy} aria-disabled={disabled} className="tw-max-h-56 tw-overflow-y-auto">
        {groups.length === 0 ? (
          <p className="tw-mb-0 tw-px-3 tw-py-3 tw-text-sm tw-text-content-muted">{emptyMessage}</p>
        ) : (
          groups.map(({ group, items }) => (
            <div key={group} role="group" aria-label={group}>
              <p
                aria-hidden="true"
                className="tw-mb-0 tw-border-b tw-border-line tw-bg-surface-alt tw-px-3 tw-py-1.5 tw-font-display tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted"
              >
                {group}
              </p>
              {items.map((choice) => {
                const selected = choice.id === value;
                return (
                  <button
                    key={choice.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={disabled}
                    onClick={() => onChange(choice.id)}
                    className={`tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-transition-colors focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:-tw-outline-offset-2 focus-visible:tw-outline-line-focus disabled:tw-cursor-default ${
                      selected
                        ? 'tw-bg-primary/10 tw-font-medium tw-text-primary'
                        : 'tw-text-content hover:tw-bg-surface-hover'
                    }`}
                  >
                    <span className="tw-min-w-0 tw-break-words">{choice.label}</span>
                    {choice.onHand !== null ? (
                      <span className="tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-content-muted">
                        {formatQuantity(choice.onHand, choice.unit)}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MaterialPicker;
